import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  TextField, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  MenuItem,
  CircularProgress,
  Chip
} from '@mui/material';
import { Plus, Edit, Trash2, Eye, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../Config/config';

const TemplatesManagement = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [techStacks, setTechStacks] = useState([]);
  const [roles, setRoles] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    tech_stack_id: '',
    description: '',
    roles: [{ role_id: '', role_count: 1, priority: 1 }]
  });

  useEffect(() => {
    fetchTemplates();
    fetchTechStacks();
    fetchRoles();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/templates`);
      if (res.data.success) setTemplates(res.data.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchTechStacks = async () => {
    const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/tech-stacks`);
    if (res.data.success) setTechStacks(res.data.data);
  };

  const fetchRoles = async () => {
    const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/roles`);
    if (res.data.success) setRoles(res.data.data);
  };

  const handleAddRoleRow = () => {
    setFormData({
      ...formData,
      roles: [...formData.roles, { role_id: '', role_count: 1, priority: formData.roles.length + 1 }]
    });
  };

  const handleRoleChange = (index, field, value) => {
    const newRoles = [...formData.roles];
    newRoles[index][field] = value;
    setFormData({ ...formData, roles: newRoles });
  };

  const handleSubmit = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/nextgen-builder/templates`, formData);
      setOpenDialog(false);
      fetchTemplates();
      setFormData({ name: '', tech_stack_id: '', description: '', roles: [{ role_id: '', role_count: 1, priority: 1 }] });
    } catch (err) { console.error(err); }
  };

  return (
    <Box p={4} sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton 
            onClick={() => navigate('/nextgen/projects')} 
            sx={{ 
              color: '#843FEF', 
              bgcolor: 'white', 
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              '&:hover': { bgcolor: '#f0eaff' }
            }}
          >
            <ArrowLeft size={24} />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight="800" sx={{ color: '#1e293b', letterSpacing: '-0.02em' }}>
              Team Templates
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Manage automatic team generation templates
            </Typography>
          </Box>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<Plus size={18} />}
          onClick={() => setOpenDialog(true)}
          sx={{ 
            bgcolor: '#843FEF', 
            '&:hover': { bgcolor: '#7035cc' }, 
            borderRadius: '12px', 
            px: 4,
            py: 1.2,
            textTransform: 'none',
            fontWeight: 'bold',
            boxShadow: '0 4px 14px 0 rgba(132, 63, 239, 0.39)'
          }}
        >
          New Template
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={10}><CircularProgress sx={{ color: '#843FEF' }} /></Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: '600', color: '#64748b', py: 2.5 }}>TEMPLATE NAME</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#64748b' }}>TECH STACK</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#64748b' }}>DESCRIPTION</TableCell>
                <TableCell sx={{ fontWeight: '600', color: '#64748b' }} align="right">ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {templates.map((t) => (
                <TableRow key={t.id} hover sx={{ '& td': { py: 2, borderBottom: '1px solid #f1f5f9' } }}>
                  <TableCell>
                    <Typography fontWeight="700" color="#1e293b">{t.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={t.tech_stack_name} 
                      size="small" 
                      sx={{ bgcolor: '#eef2ff', color: '#4f46e5', fontWeight: '600', borderRadius: '6px' }} 
                    />
                  </TableCell>
                  <TableCell color="#475569">{t.description}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" sx={{ color: '#64748b' }}><Eye size={18} /></IconButton>
                    <IconButton size="small" sx={{ color: '#64748b' }}><Edit size={18} /></IconButton>
                    <IconButton size="small" color="error"><Trash2 size={18} /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogTitle>Create Team Template</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField 
              label="Template Name" 
              fullWidth 
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
            />
            <TextField 
              select 
              label="Associated Tech Stack" 
              fullWidth
              value={formData.tech_stack_id}
              onChange={(e) => setFormData({...formData, tech_stack_id: e.target.value})}
            >
              {techStacks.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
            </TextField>
            <TextField 
              label="Description" 
              fullWidth 
              multiline 
              rows={2}
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
            
            <Typography variant="subtitle1" fontWeight="bold" mt={2}>Roles & Allocation</Typography>
            {formData.roles.map((r, index) => (
              <Box key={index} display="flex" gap={2} alignItems="center">
                <TextField 
                  select 
                  label="Role" 
                  sx={{ flex: 2 }}
                  value={r.role_id}
                  onChange={(e) => handleRoleChange(index, 'role_id', e.target.value)}
                >
                  {roles.map(role => <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>)}
                </TextField>
                <TextField 
                  label="Count" 
                  type="number" 
                  sx={{ flex: 1 }}
                  value={r.role_count}
                  onChange={(e) => handleRoleChange(index, 'role_count', e.target.value)}
                />
                <TextField 
                  label="Priority" 
                  type="number" 
                  sx={{ flex: 1 }}
                  value={r.priority}
                  onChange={(e) => handleRoleChange(index, 'priority', e.target.value)}
                />
                <IconButton color="error" onClick={() => {
                  const newRoles = formData.roles.filter((_, i) => i !== index);
                  setFormData({ ...formData, roles: newRoles });
                }}>
                  <Trash2 size={18} />
                </IconButton>
              </Box>
            ))}
            <Button startIcon={<Plus size={16} />} onClick={handleAddRoleRow}>Add Role</Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ bgcolor: '#843FEF' }}>Save Template</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplatesManagement;
